/**
 * The sprite registry: which sheets a component is made of, as data.
 *
 * The pipeline around this file -- what generates what, and how to change it -- is written up in
 * `docs/sprite-lab.md`.
 *
 * This is the shape `lpc-forge.ts`'s branching turns into. An entry is either
 *
 *   - **templated** -- a list of layers, each a z-index and a path over `{sex}` and `{variant}`,
 *     plus the map from the game's `Color` names to the variant names the art actually uses; or
 *   - **explicit** -- a flat `sex/colour -> sheets` table, for the handful of components whose
 *     shape no template describes.
 *
 * The two exist because the registry is DERIVED from the forge and verified against it (see
 * tools/src/derive-lpc-registry.ts): anything the template form cannot reproduce exactly falls
 * back to the table rather than shipping a near-miss.
 *
 * ## What is deliberately not here
 *
 * The colour list. `HairColors`, `RaceColors` and the rest stay the source of which colours a
 * component offers and, because the first entry is the fallback, of what an unrecognised colour
 * renders as. The registry only says where a colour's art lives. Keeping the list in one place
 * means adding a colour is still one edit, and `slot_colors.ts` is where both sides read it.
 */
import { Color, Sex } from "../character/character"
import { LpcSheet, LpcSheetType } from "./lpc"

export interface LpcRegistryLayer {
    /** zIndex in the assembled character. Below 10 is behind the body. */
    z: number
    /** Path under `lpc/spritesheets/`, with `{sex}` and `{variant}` placeholders. */
    path: string
    type?: LpcSheetType
}

export interface LpcRegistryExplicitSheet {
    z: number
    path: string
    type?: LpcSheetType
}

export interface LpcRegistryEntry {
    /**
     * The colours this component offers, in declaration order.
     *
     * Distinct from `variants`, which maps a colour to the name the ART uses and only exists
     * where the path actually varies by colour. 34 components have exactly one colour and one
     * sheet -- `weapon/longsword`, `eyes/cyclops`, `bauldron` -- so they have no `variants` at
     * all, and without this the registry would know their path but not that the game calls that
     * colour `metal`. The first entry is the fallback for an unrecognised colour, so order is
     * load-bearing.
     */
    colors?: string[]
    layers?: LpcRegistryLayer[]
    /** Game `Color` -> the variant name in the art's own path. Absent when colour makes no difference. */
    variants?: Record<string, string>
    /** `"<sex>/<colour>" -> sheets`, for shapes no template covers. Mutually exclusive with `layers`. */
    explicit?: Record<string, LpcRegistryExplicitSheet[]>
}

/** slot -> component -> entry. */
export type LpcRegistry = Record<string, Record<string, LpcRegistryEntry>>

export interface ExpandArgs {
    /** The colour AFTER the fallback has been applied. Undefined when the component has none. */
    color?: Color
    /** What the caller originally asked for; only an explicit table needs it. */
    requestedColor: Color
    sex: Sex
}

/**
 * One entry, for one (colour, sex), as the sheets a character is assembled from.
 *
 * Order is the entry's own order, NOT sorted by z-index -- the forge does not sort either, and
 * callers (LpcComposite, the sprite lab) sort the flattened list themselves. A cape's front layer
 * really does come back before the layer that belongs behind the body.
 */
export const expandEntry = (entry: LpcRegistryEntry, { color, requestedColor, sex }: ExpandArgs): LpcSheet[] => {
    if (!entry) {
        return []
    }

    if (entry.explicit) {
        // An explicit table is keyed by what was ASKED for, not by the resolved colour: it was
        // captured per requested colour and already has the fallback baked into each row.
        const found = entry.explicit[`${sex}/${requestedColor}`]
        return (found || []).map(s => ({
            entityId: s.path,
            zIndex: s.z,
            ...(s.type ? { type: s.type } : {}),
        }))
    }

    if (!entry.layers) {
        return []
    }

    // A component with a variant map that has nothing for this colour has no art to point at.
    // (Only reachable when the component declares no colours at all, since the fallback otherwise
    // guarantees a supported one.)
    const variant = entry.variants ? entry.variants[color] : undefined
    if (entry.variants && variant === undefined) {
        return []
    }

    return entry.layers.map(layer => ({
        entityId: layer.path.replace("{sex}", sex).replace("{variant}", variant ?? ""),
        zIndex: layer.z,
        ...(layer.type ? { type: layer.type } : {}),
    }))
}
